import { useState } from 'react'
import { useForm, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Combobox } from '@/components/ui/combobox'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { TaxRegistrationTypeOption } from '@/types'
import { FieldInputs, validateFields } from '@/components/applicant/FieldInputs'
import { ArrowRight, Clock, DollarSign } from 'lucide-react'

type CountryOption = { id: string; code: string; name: string }
type StateOption = { id: string; code: string; name: string; country_id: string }
type BusinessOption = { id: string; name: string; country_id: string | null; state_id: string | null }

type Props = {
  countries: CountryOption[]
  states: StateOption[]
  types: TaxRegistrationTypeOption[]
  businesses: BusinessOption[]
}

export default function TaxRegistrationsNew({ countries, states, types, businesses }: Props) {
  const basePath = useBasePath()
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [fieldValues, setFieldValues] = useState<Record<string, string | number | string[]>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const countryStates = states.filter(s => s.country_id === selectedCountryId)
  const hasStates = countryStates.length > 0

  const filteredTypes = types.filter(t => {
    if (!selectedCountryId) return false
    if (t.country_id !== selectedCountryId) return false
    if (t.state_id) {
      if (!selectedStateId) return false
      if (t.state_id !== selectedStateId) return false
    }
    return true
  })

  const selectedType = types.find(t => t.id === selectedTypeId)

  const form = useForm<Record<string, any>>({
    tax_registration_type_id: '',
    business_id: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedType) return
    const errors = validateFields(selectedType.fields, fieldValues)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }
    router.post(`${basePath}/tax_registrations`, {
      tax_registration: {
        ...form.data,
        field_values: fieldValues
      }
    })
  }

  return (
    <DashboardLayout title="New Tax Registration" breadcrumbs={[{ label: 'Tax Registrations', href: 'tax_registrations' }, { label: 'New Registration' }]}>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">New Tax Registration</h1>
        <p className="text-sm text-muted-foreground">Apply for a tax registration</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Business */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
            <h2 className="text-base font-medium text-foreground">Business</h2>
          </div>
          <Combobox
            options={businesses.map(b => ({ value: b.id, label: b.name }))}
            value={selectedBusinessId}
            onValueChange={(value) => {
              setSelectedBusinessId(value)
              form.setData('business_id', value)
              const biz = businesses.find(b => b.id === value)
              if (biz?.country_id) {
                setSelectedCountryId(biz.country_id)
                setSelectedStateId(biz.state_id || '')
              } else {
                setSelectedCountryId('')
                setSelectedStateId('')
              }
              setSelectedTypeId('')
              form.setData('tax_registration_type_id', '')
            }}
            placeholder="Select business..."
            searchPlaceholder="Search businesses..."
            emptyMessage="No businesses found."
          />
        </section>

        {/* Jurisdiction */}
        {selectedBusinessId && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
              <h2 className="text-base font-medium text-foreground">Jurisdiction</h2>
            </div>
            {(() => {
              const biz = businesses.find(b => b.id === selectedBusinessId)
              if (biz?.country_id) {
                const country = countries.find(c => c.id === biz.country_id)
                const state = states.find(s => s.id === biz.state_id)
                return (
                  <div className="px-3 py-2.5 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Auto-selected: </span>
                    <span className="font-medium">{[state?.name, country?.name].filter(Boolean).join(', ')}</span>
                  </div>
                )
              }
              return (
                <div className={`grid gap-3 ${hasStates ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Country</Label>
                    <Combobox
                      options={countries.map(c => ({ value: c.id, label: c.name }))}
                      value={selectedCountryId}
                      onValueChange={(v) => { setSelectedCountryId(v); setSelectedStateId(''); setSelectedTypeId(''); form.setData('tax_registration_type_id', '') }}
                      placeholder="Select country..."
                      searchPlaceholder="Search countries..."
                      emptyMessage="No countries found."
                    />
                  </div>
                  {hasStates && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <Combobox
                        options={countryStates.map(s => ({ value: s.id, label: s.name }))}
                        value={selectedStateId}
                        onValueChange={(v) => { setSelectedStateId(v); setSelectedTypeId(''); form.setData('tax_registration_type_id', '') }}
                        placeholder="Select state..."
                        searchPlaceholder="Search states..."
                        emptyMessage="No states found."
                      />
                    </div>
                  )}
                </div>
              )
            })()}
          </section>
        )}

        {/* Type Selection */}
        {filteredTypes.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
              <h2 className="text-base font-medium text-foreground">Registration Type</h2>
            </div>
            <div className="space-y-1.5">
              {filteredTypes.map(t => {
                const isSelected = selectedTypeId === t.id
                return (
                  <button key={t.id} type="button" onClick={() => { setSelectedTypeId(t.id); form.setData('tax_registration_type_id', t.id); setFieldValues({}); setFieldErrors({}) }}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${isSelected ? 'border-foreground bg-muted' : 'border-border hover:border-border'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-foreground">{t.name}</span>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          {t.processing_days_min && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.processing_days_min}-{t.processing_days_max} days</span>}
                          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatCurrency(t.total_cents)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* Additional Fields */}
        {selectedType && selectedType.fields.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">4</span>
              <h2 className="text-base font-medium text-foreground">Additional Information</h2>
            </div>
            <FieldInputs
              fields={selectedType.fields}
              values={fieldValues}
              onChange={(fieldId, value) => setFieldValues(prev => ({ ...prev, [fieldId]: value }))}
              errors={fieldErrors}
            />
          </section>
        )}

        {/* Submit */}
        {selectedType && selectedBusinessId && (
          <section>
            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{selectedType.name}</span>
                  <span className="font-medium">{formatCurrency(selectedType.total_cents)}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(selectedType.total_cents)}</span>
                </div>
              </div>
              <Button type="submit" className="w-full h-9 text-sm" disabled={form.processing}>
                {form.processing ? 'Creating...' : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </form>
    </DashboardLayout>
  )
}
